import type { Entity } from '@core/types';
import { useNonPrimaryEmailLinkIdHeader } from '@queries/email/link';
import { useMarkThreadAsSeenMutation } from '@queries/email/thread';
import { debounce } from '@solid-primitives/scheduled';
import { onMount } from 'solid-js';
import { markNotificationsForEntityAsRead } from '../notification-helpers';
import type { NotificationSource } from '../notification-source';

const DEFAULT_DEBOUNCE_TIME = 2_000;

type DebouncedMarkerProps = {
  debouncedFn: () => void;
  debounceTime?: number;
};

const makeDebouncedMarker = (props: DebouncedMarkerProps): VoidFunction => {
  const debounceTime = props.debounceTime ?? DEFAULT_DEBOUNCE_TIME;

  const trigger = debounce(props.debouncedFn, debounceTime);

  return trigger;
};

function DebouncedMarker(props: DebouncedMarkerProps) {
  const triggerDebounce = makeDebouncedMarker(props);

  onMount(() => {
    triggerDebounce();
  });

  return '';
}

/**
 * Debounced component that marks a notification as read
 * @param props
 * @returns
 */
export function DebouncedNotificationReadMarker(props: {
  notificationSource: NotificationSource;
  debounceTime?: number;
  entity: Entity;
}) {
  if (props.entity.type === 'email') {
    return (
      <EmailDebouncedReadMarker
        notificationSource={props.notificationSource}
        debounceTime={props.debounceTime}
        threadId={props.entity.id}
      />
    );
  }

  return (
    <DebouncedMarker
      debounceTime={props.debounceTime}
      debouncedFn={() => {
        void markNotificationsForEntityAsRead(
          props.notificationSource,
          props.entity
        );
      }}
    />
  );
}

export function DocumentDebouncedNotificationReadMarker(props: {
  notificationSource: NotificationSource;
  debounceTime?: number;
  documentId: string;
}) {
  return (
    <DebouncedNotificationReadMarker
      notificationSource={props.notificationSource}
      debounceTime={props.debounceTime}
      entity={{
        type: 'document',
        id: props.documentId,
      }}
    />
  );
}

type ChannelDebouncedNotificationReadMarkerProps = {
  notificationSource: NotificationSource;
  debounceTime?: number;
  channelId: string;
};

export const makeDebouncedChannelNotificationReadMarker = (
  props: ChannelDebouncedNotificationReadMarkerProps
) => {
  return makeDebouncedMarker({
    debounceTime: props.debounceTime,
    debouncedFn() {
      markNotificationsForEntityAsRead(props.notificationSource, {
        type: 'channel',
        id: props.channelId,
      });
    },
  });
};

export function ChannelDebouncedNotificationReadMarker(
  props: ChannelDebouncedNotificationReadMarkerProps
) {
  return (
    <DebouncedNotificationReadMarker
      notificationSource={props.notificationSource}
      debounceTime={props.debounceTime}
      entity={{
        type: 'channel',
        id: props.channelId,
      }}
    />
  );
}

export function EmailDebouncedReadMarker(props: {
  notificationSource: NotificationSource;
  debounceTime?: number;
  threadId: string;
  /** The inbox the thread belongs to; scopes mark-as-seen to a non-primary inbox. */
  linkId?: string;
}) {
  const markSeenMutation = useMarkThreadAsSeenMutation();
  const toHeaderLinkId = useNonPrimaryEmailLinkIdHeader();

  return (
    <DebouncedMarker
      debounceTime={props.debounceTime}
      debouncedFn={() => {
        markNotificationsForEntityAsRead(props.notificationSource, {
          type: 'email_thread',
          id: props.threadId,
        });
        markSeenMutation.mutate({
          threadId: props.threadId,
          linkId: toHeaderLinkId(props.linkId),
        });
      }}
    />
  );
}
